import type { BasketItem, Category, Service } from "@/types";

/** Main service category id on a listing (`categoryId`, not subcategory tags). */
export function getServiceMainCategoryId(service: Service): string | null {
	/** API may return an id string or a populated category document. */
	const c = service.categoryId as Category | string;
	if (c == null) return null;
	if (typeof c === "string") {
		const id = c.trim();
		return id.length > 0 ? id : null;
	}
	if (c.isSubcategory === true) {
		const parent = c.parentCategoryId;
		if (parent == null) return null;
		if (typeof parent === "string") {
			const id = parent.trim();
			return id.length > 0 ? id : null;
		}
		if (typeof parent === "object") {
			const id = parent._id ?? parent.id;
			return id != null && String(id).trim() !== "" ? String(id) : null;
		}
		return null;
	}
	const id = c._id ?? c.id;
	return id != null && String(id).trim() !== "" ? String(id) : null;
}

/**
 * Hosting eligibility comes from the admin tick on **main service categories** only.
 * Prefer `mainCategoryHostingEnabled` (from `/categories`) so basket is not stuck on stale embedded category data.
 */
export function serviceCategoryShowsHosting(
	service: Service,
	mainCategoryHostingEnabled?: Readonly<Record<string, boolean>>,
): boolean {
	const mainId = getServiceMainCategoryId(service);
	if (!mainId) return false;

	if (mainCategoryHostingEnabled && Object.prototype.hasOwnProperty.call(mainCategoryHostingEnabled, mainId)) {
		return mainCategoryHostingEnabled[mainId] === true;
	}

	const c = service.categoryId;
	if (c && typeof c === "object" && c.isSubcategory !== true) {
		return c.showHosting === true;
	}

	return false;
}

/** Basket lines whose main service category has the hosting tick enabled. */
export function countHostingEligibleBasketLines(
	items: BasketItem[],
	serviceById: Record<string, Service | undefined>,
	mainCategoryHostingEnabled?: Readonly<Record<string, boolean>>,
): number {
	let count = 0;
	for (const item of items) {
		const service = serviceById[item.serviceId];
		if (service == null) continue;
		if (serviceCategoryShowsHosting(service, mainCategoryHostingEnabled)) {
			count += 1;
		}
	}
	return count;
}

export function basketHasHostingEligibleLines(
	items: BasketItem[],
	serviceById: Record<string, Service | undefined>,
	mainCategoryHostingEnabled?: Readonly<Record<string, boolean>>,
): boolean {
	return countHostingEligibleBasketLines(items, serviceById, mainCategoryHostingEnabled) > 0;
}

/** Map main category id → hosting tick (only non-subcategory rows). */
export function buildMainCategoryHostingEnabledMap(categories: Category[]): Record<string, boolean> {
	const map: Record<string, boolean> = {};
	for (const cat of categories) {
		if (cat.isSubcategory === true) continue;
		const enabled = cat.showHosting === true;
		map[cat._id] = enabled;
		if (cat.id) map[cat.id] = enabled;
	}
	return map;
}
